import ROUTING_ACTION_TYPES from './routingActionTypes';
import { RoutingPolicyCompilationError, compilePolicyCriteria } from './policyCriteriaCompiler';

const hasAttribute = (contactAttributes, attributeId) => (
  Object.prototype.hasOwnProperty.call(contactAttributes, attributeId)
);

const toValues = (value) => Array.isArray(value) ? value : [value];

const matchesCondition = (condition, contactAttributes) => {
  const exists = hasAttribute(contactAttributes, condition.attributeId);
  const contactValue = contactAttributes[condition.attributeId];
  const expectedValues = toValues(condition.value);

  switch (condition.operator) {
    case 'EQUALS':
      return exists && expectedValues.includes(contactValue);
    case 'NOT_EQUALS':
      return exists && !expectedValues.includes(contactValue);
    case 'IN':
      return exists && expectedValues.includes(contactValue);
    case 'NOT_IN':
      return exists && !expectedValues.includes(contactValue);
    case 'EXISTS':
    case 'EXISITS':
      return exists;
    case 'NOT_EXISTS':
      return !exists;
    default:
      return false;
  }
};

const matchesCriteria = (criteria, contactAttributes) => {
  if (!criteria || !Array.isArray(criteria.conditions) || criteria.conditions.length === 0) {
    return false;
  }
  const results = criteria.conditions.map((condition) => (
    Array.isArray(condition?.conditions)
      ? matchesCriteria(condition, contactAttributes)
      : matchesCondition(condition, contactAttributes)
  ));
  return criteria.logic === 'OR' ? results.some(Boolean) : results.every(Boolean);
};

const selectedGroupIds = (action) => new Set(action.routingGroupIds || []);

const applyAction = (steps, action) => {
  const groupIds = selectedGroupIds(action);
  switch (action.type) {
    case ROUTING_ACTION_TYPES.EXCLUDE_ROUTING_GROUPS:
    case ROUTING_ACTION_TYPES.SKIP_ROUTING_GROUP:
      return steps.filter((step) => !groupIds.has(step.routingGroupId));
    case ROUTING_ACTION_TYPES.ALLOWED_ROUTING_GROUPS:
      return steps.filter((step) => groupIds.has(step.routingGroupId));
    case ROUTING_ACTION_TYPES.CHANGE_TIMEOUT:
      return steps.map((step) => ({
        ...step,
        timeoutSeconds: step.timeoutSeconds == null ? step.timeoutSeconds : Number(action.timeoutSeconds)
      }));
    case ROUTING_ACTION_TYPES.SET_PRIORITY:
      return steps;
    default:
      throw new RoutingPolicyCompilationError(`Unsupported restriction action '${action.type}'.`, [{
        code: 'UNSUPPORTED_RESTRICTION_ACTION',
        message: `Unsupported restriction action '${action.type}'.`,
        path: 'actions'
      }]);
  }
};

export const restrictionMatchesContact = (restriction, contactAttributes = {}) => (
  restriction?.status !== 'inactive' && matchesCriteria(restriction?.contactCriteria, contactAttributes)
);

export const applyRestrictionToPolicy = (policy, restriction, contactAttributes = {}) => {
  if (!restrictionMatchesContact(restriction, contactAttributes)) {
    return {
      matched: false,
      policy: {
        ...policy,
        routingSteps: (policy.routingSteps || []).map((step) => ({ ...step }))
      }
    };
  }

  const transformedSteps = (restriction.actions || []).reduce(
    (steps, action) => applyAction(steps, action),
    (policy.routingSteps || []).map((step) => ({ ...step }))
  );
  const routingSteps = transformedSteps.map((step, index) => ({ ...step, step: index + 1 }));

  if (routingSteps.length === 0) {
    throw new RoutingPolicyCompilationError('Restriction removed every routing group from the policy.', [{
      code: 'NO_ROUTING_STEPS_AFTER_RESTRICTION',
      message: 'Restriction removed every routing group from the policy.',
      path: 'routingSteps'
    }]);
  }

  return { matched: true, policy: { ...policy, routingSteps } };
};

export const compileRestrictedPolicyCriteria = (
  policy,
  restriction,
  contactAttributes = {},
  routingGroups = [],
  attributes = []
) => {
  const transformed = applyRestrictionToPolicy(policy, restriction, contactAttributes);
  return {
    restrictionMatched: transformed.matched,
    appliedPolicy: transformed.policy,
    ...compilePolicyCriteria(transformed.policy, routingGroups, attributes)
  };
};

export default compileRestrictedPolicyCriteria;